import { AMOUNT_FORMAT_TYPES, AMOUNT_TYPES } from '@ezugi/constants/amount';

import getSymbolByCurrencyCode from '../core/symbol';

const addAmountToFixed = (amount: number, decimal: number): string => {
  // make sure decimal value is valid
  let finalDecimal = decimal;
  if (decimal === undefined || isNaN(decimal)) finalDecimal = 2;
  // execute to fixed command to trim decimal
  let finalAmount = Number(amount).toFixed(finalDecimal);
  // make sure if decimal is 00, trim it out
  if (Number(finalAmount) % 1 === 0) finalAmount = Number(finalAmount).toFixed(0);
  return finalAmount;
};

export const formatAmountWithSuffix = (
  amount: number,
  decimal = 2,
  config = { kSymbol: 'K', mSymbol: 'M' },
): string => {
  const parsedAmount = Number(amount);
  if (isNaN(parsedAmount)) return '0';

  const decimals = isNaN(decimal) ? 2 : decimal;
  const decimalsMilions = Math.max(1, decimals - 1);

  const truncate = (num: number, digits: number): number => {
    const factor = 10 ** digits;
    return Math.floor(num * factor) / factor;
  };

  if (parsedAmount >= 1_000_000) {
    const short = parsedAmount / 1_000_000;
    const truncated = truncate(short, decimalsMilions);
    let result = Number(truncated.toFixed(decimalsMilions)).toString() + config.mSymbol;
    if (short > truncated) result += '+';
    return result;
  }

  if (parsedAmount >= 10_000) {
    const short = parsedAmount / 1000;
    const truncated = Math.floor(short);
    let result = `${truncated}${config.kSymbol}`;
    if (parsedAmount % 1000 !== 0) {
      result += '+';
    }
    return result;
  }

  if (parsedAmount < 100) {
    return parsedAmount % 1 === 0
      ? parsedAmount.toFixed(0)
      : parsedAmount.toFixed(decimals);
  }

  return parsedAmount % 1 === 0
    ? parsedAmount.toFixed(0)
    : `${Math.floor(parsedAmount)}+`;
};

// add display amount separator ('1,000', '23,000,123')
const addAmountSeparator = (amount: string, separator: string): string => {
  const returnArr = [];
  const splitValue = String(amount).split('.'); // split value to separate value from decimal value
  const value = splitValue[0];
  const decimalValue = splitValue.length > 1 ? `.${splitValue[1]}` : '';
  const count = 3; // every 3 letters add separator
  let totalCount = 0; // keep count of chars added so far

  // loop value chars from end to start (to add separators correctly)
  for (let i = value.length - 1; i >= 0; i--) {
    // add current looped char
    returnArr.push(value.charAt(i));
    totalCount++;
    // if passed 3 letters from the end, add separator, also make sure its not the first char
    if (totalCount % count === 0 && i - 1 >= 0) returnArr.push(separator);
  }
  // since we added to arr from end, we need to reverse the array and convert it back to string, re-add decimal
  return returnArr.reverse().join('') + decimalValue;
};

// TODO: refactor with named argument
export function formatAmount(
  amount: unknown,
  amountType: string,
  formatConfig: any,
  currencyCode?: string,
  showCurrencySymbol?: boolean, // used to override the configuration attribute
  showCurrencyCode?: boolean, // used to override the configuration attribute
  hasSymbolSpacing = true,
): string {
  // make sure received value is number
  let finalAmount = Number(amount ?? 0);

  // Make sure handling negative values correctly by treating them as non negative and multiplying by -1 at the end
  const isNegative = finalAmount < 0;
  let returnValue: string | number = '0';

  finalAmount = Math.abs(finalAmount);

  // Set format type and currency display according to format type received
  let formatType: string = AMOUNT_TYPES.BET;
  let boShowCurrency = false;

  switch (amountType) {
  case AMOUNT_TYPES.BET: {
    formatType = formatConfig.betFormat;
    boShowCurrency = formatConfig.showBetCurrency;
    break;
  }
  case AMOUNT_TYPES.LIMIT: {
    formatType = formatConfig.limitFormat;
    boShowCurrency = formatConfig.showLimitCurrency;
    break;
  }
  case AMOUNT_TYPES.CHIP: {
    formatType = AMOUNT_FORMAT_TYPES.EXTEND_LETTER_CHIP;
    boShowCurrency = false;
    break;
  }
  default:
    break;
  }

  // handle each format separately
  switch (formatType) {
  // on comma, separate value by commas, only 2 digits after decimal
  case AMOUNT_FORMAT_TYPES.COMMA: {
    returnValue = addAmountSeparator(addAmountToFixed(finalAmount, 2), ',');
    break;
  }
  // on entire, leave sum as is, only 2 digits after decimal
  case AMOUNT_FORMAT_TYPES.ENTIRE: {
    returnValue = addAmountToFixed(finalAmount, 2);
    break;
  }
  // on letter, if >= 1000, divide by 1000 and display k symbol, else leave amount as is, only 2 digits after decimal
  case AMOUNT_FORMAT_TYPES.LETTER: {
    if (finalAmount >= 1000) {
      const tempValue = finalAmount / 1000;
      returnValue = addAmountToFixed(tempValue, 2);
      returnValue += formatConfig.kSymbol;
    } else {
      returnValue = addAmountToFixed(finalAmount, 2);
    }
    break;
  }
  // on extend letter, same as letter but also trim millions
  case AMOUNT_FORMAT_TYPES.EXTEND_LETTER: {
    if (finalAmount >= 1000000) {
      const tempValue = finalAmount / 1000000;
      returnValue = addAmountToFixed(tempValue, 2);
      returnValue += formatConfig.mSymbol;
    } else if (finalAmount >= 1000) {
      const tempValue = finalAmount / 1000;
      returnValue = addAmountToFixed(tempValue, 2);
      returnValue += formatConfig.kSymbol;
    } else {
      returnValue = addAmountToFixed(finalAmount, 2);
    }
    break;
  }
  // for chips K is displayed from 10000
  case AMOUNT_FORMAT_TYPES.EXTEND_LETTER_CHIP: {
    returnValue = formatAmountWithSuffix(finalAmount, 2, formatConfig);
    break;
  }
  default:
    break;
  }

  // Re-add negative value symbol if amount was negative
  if (isNegative) returnValue = `-${returnValue}`;

  // Ensure returnValue is a string
  const finalReturnValue = String(returnValue);

  if (!boShowCurrency) return finalReturnValue;

  const showSymbol = typeof showCurrencySymbol !== 'undefined'
    ? showCurrencySymbol
    : formatConfig.showCurrencySymbol;
  const showCode = typeof showCurrencyCode !== 'undefined'
    ? showCurrencyCode
    : formatConfig.showCurrencyCode;

  let result = finalReturnValue;

  // Add currency symbol
  if (showSymbol && currencyCode) {
    const symbol = getSymbolByCurrencyCode(currencyCode);
    result = (hasSymbolSpacing ? `${symbol} ${result}` : `${symbol}${result}`);
  }

  // Add currency code
  if (showCode) {
    result = `${result} ${currencyCode}`;
  }

  return result;
}
